import { useCallback, useEffect, useMemo, useState } from 'react';
import { CalendarDays, Plus, Search, Trash2 } from 'lucide-react';
import toast from 'react-hot-toast';
import {
  createAvailabilityRule,
  deleteAvailabilityRule,
  getAvailability,
  listAvailabilityRules
} from '../api/availability';
import { listProfessionals } from '../api/professionals';
import { getErrorMessage } from '../api/response';
import { listServices } from '../api/services';
import { getBranches } from '../api/tenant';
import Button from '../components/Button';
import ConfirmDialog from '../components/ConfirmDialog';
import FormField, { inputClassName } from '../components/FormField';
import PageHeader from '../components/PageHeader';
import StatusBadge from '../components/StatusBadge';
import Table from '../components/Table';
import { LoadingState } from '../components/PageState';
import { date, time } from '../utils/format';

const isoDate = (value) => value.toISOString().slice(0, 10);

const weekdayOptions = [
  { value: 1, label: 'Lunes' },
  { value: 2, label: 'Martes' },
  { value: 3, label: 'Miercoles' },
  { value: 4, label: 'Jueves' },
  { value: 5, label: 'Viernes' },
  { value: 6, label: 'Sabado' },
  { value: 7, label: 'Domingo' }
];

const daysFrom = (dateValue, count) => {
  const start = new Date(`${dateValue}T00:00:00`);
  return Array.from({ length: count }).map((_, index) => {
    const next = new Date(start);
    next.setDate(start.getDate() + index);
    return isoDate(next);
  });
};

const weekdayName = (weekday) => weekdayOptions.find((option) => Number(option.value) === Number(weekday))?.label || '-';

const emptyRuleForm = {
  target_type: 'professional',
  professional_id: '',
  service_id: '',
  branch_id: '',
  weekday: 1,
  start_time: '09:00',
  end_time: '18:00',
  slot_minutes: 30
};

const Disponibilidades = () => {
  const [slots, setSlots] = useState([]);
  const [rules, setRules] = useState([]);
  const [services, setServices] = useState([]);
  const [professionals, setProfessionals] = useState([]);
  const [branches, setBranches] = useState([]);
  const [dateValue, setDateValue] = useState(isoDate(new Date()));
  const [serviceId, setServiceId] = useState('');
  const [professionalId, setProfessionalId] = useState('');
  const [branchId, setBranchId] = useState('');
  const [view, setView] = useState('day');
  const [ruleForm, setRuleForm] = useState(emptyRuleForm);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const showBranchFilter = branches.length > 1;
  const professionalById = useMemo(() => new Map(professionals.map((item) => [Number(item.id), item])), [professionals]);
  const serviceById = useMemo(() => new Map(services.map((item) => [Number(item.id), item])), [services]);
  const branchById = useMemo(() => new Map(branches.map((item) => [Number(item.id), item])), [branches]);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [serviceData, professionalData, branchData, ruleData] = await Promise.all([
        listServices({ limit: 100 }),
        listProfessionals({ include_inactive: 1 }),
        getBranches(),
        listAvailabilityRules({ include_inactive: 1 })
      ]);
      setServices(serviceData);
      setProfessionals(professionalData);
      setBranches(branchData);
      setRules(ruleData);

      const dates = view === 'week' ? daysFrom(dateValue, 7) : [dateValue];
      const slotGroups = await Promise.all(dates.map((day) => getAvailability({
        date: day,
        service_id: serviceId || undefined,
        professional_id: professionalId || undefined,
        branch_id: branchData.length > 1 ? branchId || undefined : undefined
      })));
      setSlots(slotGroups.flat());
    } catch (error) {
      toast.error(getErrorMessage(error, 'No se pudo cargar disponibilidad'));
    } finally {
      setLoading(false);
    }
  }, [branchId, dateValue, professionalId, serviceId, view]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    if (!ruleForm.branch_id && branches.length === 1) {
      setRuleForm((current) => ({ ...current, branch_id: String(branches[0].id) }));
    }
  }, [branches, ruleForm.branch_id]);

  const availableCount = slots.filter((slot) => slot.available).length;

  const handleRuleSubmit = async (event) => {
    event.preventDefault();

    const isProfessional = ruleForm.target_type === 'professional';
    const targetId = isProfessional ? ruleForm.professional_id : ruleForm.service_id;

    if (!targetId) {
      toast.error(isProfessional ? 'Selecciona un veterinario' : 'Selecciona un servicio o examen');
      return;
    }

    try {
      setSaving(true);
      await createAvailabilityRule({
        target_type: ruleForm.target_type,
        professional_id: isProfessional ? Number(ruleForm.professional_id) : null,
        service_id: isProfessional ? null : Number(ruleForm.service_id),
        branch_id: ruleForm.branch_id ? Number(ruleForm.branch_id) : branches[0]?.id || null,
        weekday: Number(ruleForm.weekday),
        start_time: ruleForm.start_time,
        end_time: ruleForm.end_time,
        slot_minutes: Number(ruleForm.slot_minutes)
      });
      toast.success('Disponibilidad cargada');
      loadData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'No se pudo guardar la disponibilidad'));
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;

    try {
      setSaving(true);
      await deleteAvailabilityRule(deleteTarget.id);
      toast.success('Disponibilidad eliminada');
      setDeleteTarget(null);
      loadData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'No se pudo eliminar la disponibilidad'));
    } finally {
      setSaving(false);
    }
  };

  const slotColumns = [
    { key: 'date', label: 'Fecha', render: (row) => date(row.date) },
    { key: 'time', label: 'Hora', render: (row) => time(row.time) },
    { key: 'target', label: 'Recurso', render: (row) => row.professional_name || row.service_name || 'General' },
    ...(showBranchFilter ? [{
      key: 'branch_id',
      label: 'Sucursal',
      render: (row) => row.branch_name || branchById.get(Number(row.branch_id))?.name || '-'
    }] : []),
    { key: 'duration_minutes', label: 'Duracion', render: (row) => `${row.duration_minutes} min` },
    {
      key: 'available',
      label: 'Estado',
      render: (row) => (
        <StatusBadge
          label={row.available ? 'Disponible' : 'Ocupado'}
          color={row.available ? 'green' : 'slate'}
        />
      )
    }
  ];

  const ruleColumns = [
    { key: 'weekday', label: 'Dia', render: (row) => weekdayName(row.weekday) },
    {
      key: 'target',
      label: 'Recurso',
      render: (row) => row.professional_name || professionalById.get(Number(row.professional_id))?.full_name || row.service_name || serviceById.get(Number(row.service_id))?.name || '-'
    },
    ...(showBranchFilter ? [{
      key: 'branch_id',
      label: 'Sucursal',
      render: (row) => row.branch_name || branchById.get(Number(row.branch_id))?.name || '-'
    }] : []),
    { key: 'range', label: 'Horario', render: (row) => `${time(row.start_time)} - ${time(row.end_time)}` },
    { key: 'slot_minutes', label: 'Bloques', render: (row) => `${row.slot_minutes} min` },
    {
      key: 'active',
      label: 'Estado',
      render: (row) => <StatusBadge label={Number(row.is_active) === 1 ? 'Activa' : 'Inactiva'} color={Number(row.is_active) === 1 ? 'green' : 'slate'} />
    },
    {
      key: 'actions',
      label: 'Acciones',
      render: (row) => (
        <button onClick={() => setDeleteTarget(row)} className="rounded-md p-2 text-red-700 hover:bg-red-50" title="Eliminar disponibilidad">
          <Trash2 className="h-4 w-4" />
        </button>
      )
    }
  ];

  return (
    <div className="space-y-6">
      <PageHeader
        title="Disponibilidades"
        description="Carga bloques semanales para veterinarios, servicios y examenes."
      />

      <section className="rounded-lg border border-gray-200 bg-white p-4">
        <form onSubmit={handleRuleSubmit} className={`grid gap-3 ${showBranchFilter ? 'md:grid-cols-8' : 'md:grid-cols-7'}`}>
          <FormField label="Tipo">
            <select
              className={inputClassName}
              value={ruleForm.target_type}
              onChange={(event) => setRuleForm({ ...ruleForm, target_type: event.target.value, professional_id: '', service_id: '' })}
            >
              <option value="professional">Veterinario</option>
              <option value="service">Servicio/examen</option>
            </select>
          </FormField>
          {ruleForm.target_type === 'professional' ? (
            <FormField label="Veterinario" required>
              <select className={inputClassName} value={ruleForm.professional_id} onChange={(event) => setRuleForm({ ...ruleForm, professional_id: event.target.value })} required>
                <option value="">Seleccionar</option>
                {professionals.map((professional) => <option key={professional.id} value={professional.id}>{professional.full_name}</option>)}
              </select>
            </FormField>
          ) : (
            <FormField label="Servicio/examen" required>
              <select className={inputClassName} value={ruleForm.service_id} onChange={(event) => setRuleForm({ ...ruleForm, service_id: event.target.value })} required>
                <option value="">Seleccionar</option>
                {services.map((service) => <option key={service.id} value={service.id}>{service.name}</option>)}
              </select>
            </FormField>
          )}
          {showBranchFilter && (
            <FormField label="Sucursal">
              <select className={inputClassName} value={ruleForm.branch_id} onChange={(event) => setRuleForm({ ...ruleForm, branch_id: event.target.value })}>
                {branches.map((branch) => <option key={branch.id} value={branch.id}>{branch.name}</option>)}
              </select>
            </FormField>
          )}
          <FormField label="Dia">
            <select className={inputClassName} value={ruleForm.weekday} onChange={(event) => setRuleForm({ ...ruleForm, weekday: event.target.value })}>
              {weekdayOptions.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
            </select>
          </FormField>
          <FormField label="Inicio">
            <input type="time" className={inputClassName} value={ruleForm.start_time} onChange={(event) => setRuleForm({ ...ruleForm, start_time: event.target.value })} />
          </FormField>
          <FormField label="Termino">
            <input type="time" className={inputClassName} value={ruleForm.end_time} onChange={(event) => setRuleForm({ ...ruleForm, end_time: event.target.value })} />
          </FormField>
          <FormField label="Bloque">
            <input type="number" min="5" max="240" step="5" className={inputClassName} value={ruleForm.slot_minutes} onChange={(event) => setRuleForm({ ...ruleForm, slot_minutes: event.target.value })} />
          </FormField>
          <div className="flex items-end">
            <Button type="submit" icon={Plus} disabled={saving} className="w-full">{saving ? 'Guardando...' : 'Cargar'}</Button>
          </div>
        </form>
      </section>

      <section className="rounded-lg border border-gray-200 bg-white p-4">
        <div className={`grid gap-3 ${showBranchFilter ? 'md:grid-cols-6' : 'md:grid-cols-5'}`}>
          <FormField label="Vista">
            <select className={inputClassName} value={view} onChange={(event) => setView(event.target.value)}>
              <option value="day">Dia</option>
              <option value="week">Semana</option>
            </select>
          </FormField>
          <FormField label="Fecha">
            <input type="date" className={inputClassName} value={dateValue} onChange={(event) => setDateValue(event.target.value)} />
          </FormField>
          <FormField label="Servicio/examen">
            <select className={inputClassName} value={serviceId} onChange={(event) => setServiceId(event.target.value)}>
              <option value="">Todos</option>
              {services.map((service) => <option key={service.id} value={service.id}>{service.name}</option>)}
            </select>
          </FormField>
          <FormField label="Veterinario">
            <select className={inputClassName} value={professionalId} onChange={(event) => setProfessionalId(event.target.value)}>
              <option value="">Todos</option>
              {professionals.map((professional) => <option key={professional.id} value={professional.id}>{professional.full_name}</option>)}
            </select>
          </FormField>
          {showBranchFilter && (
            <FormField label="Sucursal">
              <select className={inputClassName} value={branchId} onChange={(event) => setBranchId(event.target.value)}>
                <option value="">Todas</option>
                {branches.map((branch) => <option key={branch.id} value={branch.id}>{branch.name}</option>)}
              </select>
            </FormField>
          )}
          <div className="flex items-end">
            <Button icon={Search} variant="secondary" onClick={loadData} className="w-full">Consultar</Button>
          </div>
        </div>
      </section>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        <div className="rounded-lg border border-gray-200 bg-white p-4">
          <CalendarDays className="h-5 w-5 text-blue-600" />
          <p className="mt-2 text-sm text-gray-500">Bloques disponibles</p>
          <p className="text-2xl font-bold text-gray-950">{availableCount}</p>
        </div>
        <div className="rounded-lg border border-gray-200 bg-white p-4">
          <p className="text-sm text-gray-500">Bloques ocupados</p>
          <p className="mt-2 text-2xl font-bold text-gray-950">{slots.length - availableCount}</p>
        </div>
        <div className="rounded-lg border border-gray-200 bg-white p-4">
          <p className="text-sm text-gray-500">Reglas activas</p>
          <p className="mt-2 text-2xl font-bold text-gray-950">{rules.filter((rule) => Number(rule.is_active) === 1).length}</p>
        </div>
      </div>

      <section className="rounded-lg border border-gray-200 bg-white">
        <div className="border-b border-gray-200 px-4 py-3">
          <h2 className="text-base font-semibold text-gray-950">Bloques generados</h2>
        </div>
        {loading ? <div className="p-5"><LoadingState rows={6} /></div> : <Table columns={slotColumns} data={slots} emptyMessage="No hay disponibilidad para la fecha seleccionada" />}
      </section>

      <section className="rounded-lg border border-gray-200 bg-white">
        <div className="border-b border-gray-200 px-4 py-3">
          <h2 className="text-base font-semibold text-gray-950">Reglas cargadas</h2>
        </div>
        {loading ? <div className="p-5"><LoadingState rows={4} /></div> : <Table columns={ruleColumns} data={rules} emptyMessage="No hay reglas de disponibilidad cargadas" />}
      </section>

      <ConfirmDialog
        open={Boolean(deleteTarget)}
        title="Eliminar disponibilidad"
        message="Esta regla dejara de generar bloques en la agenda."
        confirmLabel="Eliminar"
        loading={saving}
        onCancel={() => setDeleteTarget(null)}
        onConfirm={confirmDelete}
      />
    </div>
  );
};

export default Disponibilidades;
